import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, filter, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SystemNotification } from '@models/system-notification.interface';
import { FcmService } from '@core/services/fcm.service';

const BASE_URL = `${environment.api_rest_url}/system-notification`;
const DISMISSED_KEY = 'system-notifications:dismissed';

@Injectable({ providedIn: 'root' })
export class SystemNotificationService {
  private readonly http = inject(HttpClient);
  private readonly fcmService = inject(FcmService);

  private readonly notificationsState = signal<SystemNotification[]>([]);

  readonly notifications = this.notificationsState.asReadonly();
  readonly hasNotifications = computed(() => this.notificationsState().length > 0);
  readonly count = computed(() => this.notificationsState().length);

  constructor() {
    this.fcmService.pushMessage$
      .pipe(filter((n) => !this.readDismissed().has(n.id)))
      .subscribe((notification) => {
        this.notificationsState.update((current) => {
          const alreadyExists = current.some((n) => n.id === notification.id);
          return alreadyExists ? current : [notification, ...current];
        });
      });
  }

  dismiss(id: number): void {
    this.notificationsState.update((items) => items.filter((n) => n.id !== id));
    const dismissed = this.readDismissed();
    dismissed.add(id);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed]));

    this.http
      .patch(`${BASE_URL}/${id}/dismiss`, {})
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  reset(): void {
    this.notificationsState.set([]);
  }

  private readDismissed(): Set<number> {
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  }
}
